import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { submitWish, getWishes } from '../utils/api';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Wishes = ({ invitation, refreshTrigger}) => {
  const { guest_name, code } = invitation;

  const [formData, setFormData] = useState({
    code: code,
    name: guest_name,
    message: ''
  });

  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const wishesPerPage = 5; // Show 5 wishes per page

useEffect(() => {
  if (refreshTrigger > 0) {
    // If triggered by RSVP, wait for DB
    setTimeout(() => {
      loadWishes();
    }, 1000); // Additional 1 second for DB
  } else {
    loadWishes();
  }
}, [refreshTrigger]);

  const loadWishes = async () => {
    try {
      const data = await getWishes();
      if (data.success) {
        setWishes(data.wishes);
      }
    } catch (err) {
      console.error('Failed to load wishes:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await submitWish(formData);

      if (result.success) {
        setSubmitted(true);
        setFormData({ ...formData, message: '' });
        // Reload wishes to show new one
        await loadWishes();
        // Go to first page to see the new wish
        setCurrentPage(1);

        // Reset submitted state after 3 seconds
        setTimeout(() => setSubmitted(false), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit wish. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const indexOfLastWish = currentPage * wishesPerPage;
  const indexOfFirstWish = indexOfLastWish - wishesPerPage;
  const currentWishes = wishes.slice(indexOfFirstWish, indexOfLastWish);
  const totalPages = Math.ceil(wishes.length / wishesPerPage);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Smooth scroll to wishes section
    document.getElementById('wishes-list')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <section className="pb-32 px-4 md:px-8 bg-gradient-to-b from-cream to-[#F5F2EB]">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 hidden"
        >
          <h2 className="text-5xl md:text-7xl mb-6 text-pink font-display">Wishes</h2>
          <p className="text-lg text-sage mt-8">
            Leave your heartfelt wishes for the happy couple
          </p>
        </motion.div>

        {/* Wish Form */}
        <motion.div
          className="p-6 bg-white md:p-12 rounded-3xl shadow-lg mb-16 hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Name Field - Pre-filled */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                readOnly
                required
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-3xl focus:outline-none focus:border-sage transition-all pointer-events-none"
              />
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-charcoal mb-2">
                Your Wishes
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
                maxLength="500"
                placeholder="Write your heartfelt wishes here..."
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-3xl focus:outline-none focus:border-sage transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                {formData.message.length}/500 characters
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-3xl text-red-600 text-center">
                {error}
              </div>
            )}

            {/* Success Message */}
            {submitted && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 bg-green-50 border border-green-200 rounded-3xl text-green-600 text-center"
              >
                ✨ Thank you! Your wish has been sent.
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !formData.message.trim()}
              className="w-full py-4 bg-terracotta text-white rounded-full font-medium text-lg hover:bg-terracotta/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Wishes'}
            </button>
          </form>
        </motion.div>

        <div className="mb-8 text-center">
          <h2 className="text-2xl md:text-5xl mb-6 text-pink font-display">
            Dear <br />
            <span className='whitespace-nowrap text-5xl md:text-7xl'>Caleb & Alyssa</span>
          </h2>
        </div>

        {/* Display Wishes */}
        <motion.div
          id="wishes-list"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white rounded-3xl p-6 md:p-12 shadow-md"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xs font-body text-charcoal">
              {wishes.length} {wishes.length === 1 ? 'Wish' : 'Wishes'}
            </h3>
            <div className="text-xs text-sage-dark">
              Page {currentPage} of {totalPages || 1}
            </div>
          </div>

          <div className="space-y-6">
            {currentWishes.map((wish, index) => (
              <motion.div
                key={wish.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-cream p-4 md:p-6 rounded-3xl border-l-4 border-pink"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold font-body text-sage-dark text-sm">
                    {wish.name}
                  </h4>
                  <span className="text-[10px] text-sage-dark whitespace-nowrap">
                    {wish.date}
                  </span>
                </div>
                <p className="text-charcoal leading-relaxed text-xs">
                  {wish.message}
                </p>
              </motion.div>
            ))}

            {wishes.length === 0 && (
              <div className="text-center py-12 text-sage-dark">
                <p className="text-lg italic">
                  Be the first to leave a wish! 💝
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {wishes.length > wishesPerPage && (
            <div className="mt-8 flex justify-center items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-3 rounded-full bg-cream hover:bg-sage hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-cream disabled:hover:text-charcoal"
              >
                <FaChevronLeft />
              </button>

              {/* Page Numbers */}
              <div className="flex gap-2">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;

                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`w-10 h-10 rounded-full font-medium transition-all ${
                          currentPage === pageNumber
                            ? 'bg-sage text-white shadow-lg'
                            : 'bg-cream hover:bg-sage/20'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber} className="w-10 h-10 flex items-center justify-center">...</span>;
                  }
                  return null;
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-3 rounded-full bg-cream hover:bg-sage hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-cream disabled:hover:text-charcoal"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Wishes;
